import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Avatar,
  Badge,
  Button,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

import styles from "./Navbar.module.css";
import logo from "../../assets/img/logo.png";
import menuIcon from "../../assets/svg/menuIcon.svg";
import closeIcon from "../../assets/svg/closeIcon.svg";
import { useAuth } from "../../contexts/AuthContext";
import SearchBar from "../SearchBar/SearchBar";
import ChatModal from "../RealtorAI/ChatModal";

const Navbar = () => {
  const navigate = useNavigate();
  const { accessToken, userDetails, showLoginModal } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [allNotifications, setAllNotifications] = useState([]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (accessToken && userDetails?.id) {
      const fetchNotifications = async () => {
        try {
          const [res1, res2] = await Promise.all([
            axios.get(
              `${
                import.meta.env.VITE_BACKEND_URL
              }/reservation/notifications?user_id=${userDetails.id}`,
              {
                headers: { Authorization: accessToken },
              }
            ),
            axios.get(
              `${
                import.meta.env.VITE_BACKEND_URL
              }/reservation/message-notifications?user_id=${userDetails.id}`,
              {
                headers: { Authorization: accessToken },
              }
            ),
          ]);

          const reservationData = res1.data.success
            ? res1.data.data.map((n) => ({ ...n, type: "reservation" }))
            : [];
          const messageData = res2.data.success
            ? res2.data.data.map((n) => ({ ...n, type: "message" }))
            : [];

          const unread = [...reservationData, ...messageData].filter(
            (n) => !n.is_read
          );
          const combined = [...unread, ...reservationData, ...messageData]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 10);

          setAllNotifications(combined);
          setUnreadCount(unread.length);
        } catch (error) {
          console.error("Failed to fetch notifications:", error);
        }
      };
      fetchNotifications();
    }
  }, [accessToken, userDetails]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleNotificationClick = (notif) => {
    const endpoint =
      notif.type === "reservation"
        ? `/reservation/notifications/${notif.id}/mark-read`
        : `/reservation/message-notifications/${notif.id}/mark-read`;

    axios.put(
      `${import.meta.env.VITE_BACKEND_URL}${endpoint}`,
      {},
      {
        headers: { Authorization: accessToken },
      }
    );

    setAllNotifications((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => prev - 1);

    if (notif.type === "reservation") {
      navigate("/MyFirstLand?category=scheduling");
    } else {
      navigate(
        `/MyFirstLand?category=messages&chatId=${notif.chat_id}&targetUserId=${notif.sender_id}`
      );
    }

    setAnchorEl(null);
  };

  return (
    <>
      <nav className={styles.navbar}>
        <div className={styles.left}>
          <div className={styles.burgerMenu} onClick={handleMobileMenuToggle}>
            <img
              src={isMobileMenuOpen ? closeIcon : menuIcon}
              alt="menu"
              className={styles.burgerIconImage}
            />
          </div>
          <Link to="/" className={styles.logoContainer}>
            <img src={logo} alt="FirstLand" />
          </Link>
        </div>

        <div className={styles.right}>
          {accessToken && (
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              color="inherit"
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          )}
          {accessToken ? (
            <Link to="/MyFirstLand?category=profile">
              <div className={styles.profilePic}>
                <img src={userDetails?.photoUrl} alt="profile" />
              </div>
            </Link>
          ) : (
            <Button onClick={showLoginModal} className={styles.loginBtn}>
              Sign In
            </Button>
          )}
        </div>

        <div className={styles.center}>
          <SearchBar text="Services" />
          <div className={styles.navLinks}>
            <Link to="/About">About</Link>
            <Link to="/Education">Education</Link>
            {accessToken && (
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="inherit"
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            {accessToken ? (
              <Link to="/MyFirstLand?category=profile">
                <div className={styles.profilePic}>
                  {userDetails?.photoUrl ? (
                    <img src={userDetails?.photoUrl} alt="Profile Pic"></img>
                  ) : (
                    <Avatar
                      className={styles.ppAvatar}
                      alt="Profile Pic"
                      src=""
                    />
                  )}
                </div>
              </Link>
            ) : (
              <Button onClick={showLoginModal} className={styles.loginBtn}>
                Sign In
              </Button>
            )}
            <Link to="/realtor-landing" className={styles.proPlansLink}>
              <Button className={styles.proPlansButton}>Go Pro</Button>
            </Link>
            <Link to="/subscription-plans" className={styles.proPlansLink}>
              <Button className={styles.proPlansButton}>Pro Plans</Button>
            </Link>
            <ChatModal />
          </div>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <SearchBar text="Services" />
          <Link to="/About">About</Link>
          <Link to="/Education">Education</Link>

          <Link to="/realtor-landing"> Go Pro</Link>
          <Link to="/subscription-plans"> Pro Plans</Link>
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 300 }}>
          <Typography sx={{ p: 2, fontWeight: 600 }}>Notifications</Typography>
          <List>
            {allNotifications.length === 0 ? (
              <Typography sx={{ p: 2 }}>No notifications available.</Typography>
            ) : (
              allNotifications.map((n) => (
                <ListItem key={n.id} disablePadding>
                  <ListItemButton onClick={() => handleNotificationClick(n)}>
                    <ListItemText
                      primaryTypographyProps={{
                        fontWeight: n.is_read ? "normal" : "bold",
                      }}
                      primary={
                        n.type === "reservation"
                          ? n.message
                          : `New message from ${n.full_name}`
                      }
                      secondary={n.message}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};

export default Navbar;
